import React from "react"

import { Subtract } from "utility-types"

import { DateOcrExtractionProvider } from "./DateOcrExtractionProvider"
import { DateOcrProviderContext } from "./DateOcrProviderContext"

export interface WithDateOcrProvider {
  /** OCR provider injected from DateOcrProviderContext. */
  contextOcrProvider: DateOcrExtractionProvider
}

/**
 * HoC function that injects the DateOcrProviderContext value as the
 * `contextOcrProvider` property.
 *
 * @see https://github.com/typescript-cheatsheets/react-typescript-cheatsheet/blob/master/HOC.md
 */
export const withDateOcrProvider = <
  S extends WithDateOcrProvider = WithDateOcrProvider,
  D = Subtract<S, WithDateOcrProvider>
>(
  WrappedComponent: React.ComponentType<S>,
): React.FunctionComponent<D> => {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || "Component"

  const wrapper: React.FunctionComponent<D> = (props: D) => (
    <DateOcrProviderContext.Consumer>
      {(contextOcrProvider) => (
        <WrappedComponent
          {...(props as unknown as S)}
          contextOcrProvider={contextOcrProvider}
        />
      )}
    </DateOcrProviderContext.Consumer>
  )

  wrapper.displayName = `withDateOcrProvider(${displayName})`

  return wrapper
}
